import { Modal } from "@msys/ui";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useSnackbar } from "notistack";
import React from "react";

interface Props {
  quoteData: unknown;
  handleClose: () => void;
}

export const QuoteDataInspectorModal = ({ quoteData, handleClose }: Props) => {
  const { t } = useTranslate(["Quote", "Global"]);
  const { enqueueSnackbar } = useSnackbar();

  const json = React.useMemo(
    () => JSON.stringify(quoteData, null, 2),
    [quoteData]
  );

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      enqueueSnackbar(t("Copied to clipboard", { ns: "Global" }), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("Failed to copy", { ns: "Global" }), {
        variant: "error",
      });
    }
  };

  return (
    <Modal
      title={t("Quote data", { ns: "Quote" })}
      dialogProps={{ maxWidth: "lg", fullWidth: true }}
      actionButtons={[
        {
          label: t("Close", { ns: "Global" }),
          handleClick: handleClose,
          buttonProps: { variant: "text" },
        },
      ]}
      handleClose={handleClose}
    >
      <Box
        sx={{
          position: "relative",
        }}
      >
        <Tooltip title={t("Copy JSON", { ns: "Quote" })}>
          <IconButton
            size="small"
            aria-label={t("Copy JSON", { ns: "Quote" })}
            onClick={handleCopy}
            sx={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
          >
            <CopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box
          component="pre"
          sx={{
            fontFamily: "monospace",
            fontSize: 12,
            backgroundColor: theme => theme.palette.grey[100],
            border: theme => `1px solid ${theme.palette.grey[300]}`,
            borderRadius: 1,
            padding: 2,
            margin: 0,
            maxHeight: "60vh",
            overflow: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {json}
        </Box>
      </Box>
    </Modal>
  );
};
